// src/utils/useWakeLock.ts
// ─────────────────────────────────────────────────────────────
//  Zabraňuje uspání displeje po dobu rozhodčování
//
//  Primárně: Screen Wake Lock API (Chrome Android, Safari 16.4+)
//  Fallback:  přehrávání tichého nekonečného videa (starší iOS)
// ─────────────────────────────────────────────────────────────

import { useEffect, useRef } from "react";

export function useWakeLock(enabled: boolean): void {
  const wakeLockRef = useRef<any>(null);
  const videoRef    = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!enabled) {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.remove();
        videoRef.current = null;
      }
      return;
    }

    // ── Screen Wake Lock API (moderní prohlížeče) ─────────────
    if ("wakeLock" in navigator) {
      // Jen dokud efekt platí: uvolnění v úklidu (odchod z bodování, konec
      // závodu) spustí událost „release“ – bez příznaku by si zámek hned vzal
      // znovu a displej by nezhasínal ani mimo bodování
      let active = true;
      // Žádost právě běží (fáze 3, návrh 5.10): první získání a návrat na
      // stránku (visibilitychange) by jinak obě prošly kontrolou a jeden
      // zámek by zůstal viset bez reference
      let requesting = false;
      const acquire = async () => {
        if (!active || wakeLockRef.current || requesting) return;
        requesting = true;
        try {
          const sentinel = await (navigator as any).wakeLock.request("screen");
          requesting = false;
          if (!active || wakeLockRef.current) {
            sentinel.release().catch(() => {});
            return;
          }
          wakeLockRef.current = sentinel;
          sentinel.addEventListener("release", () => {
            if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
            // Automaticky obnovíme po návratu na stránku
            if (active && document.visibilityState === "visible") acquire();
          });
        } catch {
          requesting = false;
        }
      };
      acquire();

      const onVisibility = () => {
        if (document.visibilityState === "visible") acquire();
      };
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        active = false;
        document.removeEventListener("visibilitychange", onVisibility);
        const current = wakeLockRef.current;
        wakeLockRef.current = null;
        current?.release().catch(() => {});
      };
    }

    // ── Fallback: tiché video (starší iOS Safari) ─────────────
    // Přehrávání videa zabraňuje uspání na iOS Safari < 16.4
    const video = document.createElement("video");
    video.setAttribute("loop", "");
    video.setAttribute("playsinline", "");
    video.muted = true;
    video.style.cssText =
      "position:fixed;top:-1px;left:-1px;width:1px;height:1px;opacity:0;pointer-events:none;";
    // Minimální prázdné MP4 jako data URL
    video.src =
      "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAu9tZGF0AAACowYF//+nzEXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDEyOSByMjI0NSBiOThlMzI4IC0gSC4yNjQvTVBFRy00IEFWQ2NvZGVjIC0gQ29weWxlZnQgMjAwMy0yMDE0IC0gaHR0cDovL3d3dy52aWRlb2xhbi5vcmcveDI2NC5odG1sIC0gb3B0aW9uczogY2FiYWM9MSByZWY9MyBkZWJsb2NrPTE6MDowIGFuYWx5c2U9MHgzOjB4MTEzIG1lPWhleCBzdWJtZT03IHBzeT0xIHBzeV9yZD0xLjAwOjAuMDAgbWl4ZWRfcmVmPTEgbWVfcmFuZ2U9MTYgY2hyb21hX21lPTEgdHJlbGxpcz0xIDh4OGRjdD0xIGNxbT0wIGRlYWR6b25lPTIxLDExIGZhc3RfcHNraXA9MSBjaHJvbWFfcXBfb2Zmc2V0PS0yIHRocmVhZHM9NjAgbG9va2FoZWFkX3RocmVhZHM9NSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEwIHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAAFGWIhAA3//728P4FNjuYQAAAAwAAAwAAFgkJNQ==";
    document.body.appendChild(video);
    video.play().catch(() => {});
    videoRef.current = video;

    return () => {
      video.pause();
      video.remove();
      videoRef.current = null;
    };
  }, [enabled]);
}
